import {FC, ReactNode} from 'react'
import {useDispatch} from 'react-redux'
import {FieldPath, FormProvider, SubmitHandler, useFieldArray, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import Button from 'jbc-front/components/Button'
import * as FileDrop from 'jbc-front/components/presenters/form/FileDrop'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {useMutation, useQuery} from 'hooks/graphql'
import {eachValidationErrorsForHookFrom, ServerValidationError} from 'hooks/graphql/error/ServerValidationError'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {ProcedureContext, ReportFormProps} from 'reportForms/ReportForm'
import {Panel, Body, Head} from 'components/ui/Panel'
import {asyncError} from 'store/actions/asyncError'
import {fileFormFormat} from 'libs/formatter'
import {
  PROCEDURE_ATTACHMENTS_FORM,
  ProcedureAttachmentsFormQueryResult,
  ProcedureAttachmentsFormQueryVariables,
  UPDATE_PROCEDURE_ATTACHMENTS,
  UpdateProcedureAttachmentsQueryVariables,
  UpdateProcedureAttachmentsQueryResult,
} from './query'
import {Schema, schema} from './schema'
import styles from './AttachmentsForm.scss'

// @ts-ignore
import {titleMap} from 'procedureStatuses/Show'

export const AttachmentsForm: FC<ReportFormProps> = ({
  procedureStatusId,
  submitText,
  afterSubmit,
  procedureContext,
  comments,
  renderReject,
}) => {
  const {data, loading} = useQuery<ProcedureAttachmentsFormQueryResult, ProcedureAttachmentsFormQueryVariables>(
    PROCEDURE_ATTACHMENTS_FORM,
    {
      variables: {procedureStatusId},
      fetchPolicy: 'network-only',
    }
  )
  const procedureStatus = data?.client?.procedureStatus

  if (loading || !procedureStatus) {
    return null
  }

  return (
    <div>
      <div className="l-title-wrap">
        <h1 className="m-title-main">{titleMap[procedureStatus.procedureType]}</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form
        procedureStatus={procedureStatus}
        submitText={submitText}
        afterSubmit={afterSubmit}
        procedureContext={procedureContext}
        comments={comments}
        renderReject={renderReject}
      />
    </div>
  )
}

const makeDefaultValues = (ps: ProcedureAttachmentsFormQueryResult['client']['procedureStatus']) => {
  return {
    attachments: ps.procedureAttachments.map((attachment) => ({
      id: attachment.id,
      file: fileFormFormat(attachment.file),
    })),
  }
}

interface FormProps {
  procedureStatus: ProcedureAttachmentsFormQueryResult['client']['procedureStatus']
  submitText: string
  afterSubmit: ReportFormProps['afterSubmit']
  procedureContext: ProcedureContext
  comments?: ReactNode
  renderReject?: ReportFormProps['renderReject']
}

const Form: FC<FormProps> = ({procedureStatus, submitText, afterSubmit, procedureContext, comments, renderReject}) => {
  const dispatch = useDispatch()
  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus),
    resolver: yupResolver(schema),
    mode: 'onTouched',
  })
  const {
    handleSubmit,
    control,
    setError,
    formState: {errors, isSubmitting},
  } = methods
  const {fields, append, remove} = useFieldArray({control, name: 'attachments', keyName: '_key'})

  const [updateProcedureAttachments] = useMutation<
    UpdateProcedureAttachmentsQueryResult,
    UpdateProcedureAttachmentsQueryVariables
  >(UPDATE_PROCEDURE_ATTACHMENTS)

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    try {
      await updateProcedureAttachments({
        variables: {
          input: {
            procedureStatusId: procedureStatus.id,
            attachments: data.attachments.map((attachment) => ({
              id: attachment.id,
              file: attachment.file,
            })),
            byAdmin: procedureContext === 'admin',
          },
        },
      })
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema, data, (path, error) => {
          setError(path as FieldPath<Schema>, error)
        })
        window.scrollTo({top: 0, behavior: 'smooth'})
        return
      }
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  return (
    <FormProvider {...methods}>
      <div className="basic_information l-wrap-xs">
        <FormErrors errors={errors} isSubmitting={isSubmitting} />
        {comments}

        <Panel className="u-mt20">
          <Head>添付ファイル</Head>
          <Body>
            <FileDrop.Dropzone
              multiple
              maxFiles={10}
              onDrop={(files) => append(files.map((file) => ({file})))}
              hint="1ファイル最大アップロードサイズ：10MB"
            />
            <FileDrop.FilesHeader>アップロードファイル</FileDrop.FilesHeader>
            <FileDrop.Files>
              {fields.map((attachment, index) => (
                <div key={attachment._key}>
                  <FileDrop.File file={attachment.file} onRemove={() => remove(index)} />
                  {errors.attachments?.[index]?.file && (
                    <div className={styles.fileError}>{errors.attachments[index].file.message}</div>
                  )}
                </div>
              ))}
            </FileDrop.Files>
          </Body>
        </Panel>

        <div className="u-ta-c u-mt50">
          {renderReject?.({isSubmitting})}
          <Button primary onClick={handleSubmit(onSubmit, handleSubmitError)} disabled={isSubmitting}>
            {submitText}
          </Button>
        </div>
      </div>
    </FormProvider>
  )
}
